import { html } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { Overlay } from "@omegagrid/core";
import { Code } from "@omegagrid/code";
import { GridEvent, Grid, GridSource } from "@omegagrid/grid";
import { msg } from "@omegagrid/localize";
import { AppComponent } from "../component";
import { style } from "./formulaEditor.style";
import { FormulaEditorAdapter, FormulaEditorOptions } from "./formulaEditorAdapter";

const CODE_EDITOR_OPTIONS = { //monaco.editor.IStandaloneEditorConstructionOptions
	renderValidationDecorations: 'off',
	language: 'javascript',
	minimap: { enabled: false },
};

@customElement('rp-editor-formula')
export class FormulaEditor extends AppComponent {
	
	static styles = [style];

	@property({type: Object})
	adapter: FormulaEditorAdapter<FormulaEditor>;

	@property({ type: Object })
	options: FormulaEditorOptions;

	@query('og-overlay')
	loader: Overlay;

	@query('og-code')
	codeEditor: Code;

	@query('#variables')
	variablesDiv: HTMLElement;

	@query('og-grid')
	variablesGrid: Grid;

	layout() {
		this.codeEditor?.layout();
	}

	async willUpdate(props: Map<PropertyKey, unknown>) {
		super.willUpdate(props);
		if (props.has('adapter')) {
			this.options = await this.adapter.getOptions();
			this.adapter.editor = this;
		}
	}

	async updated(changedProperties: Map<PropertyKey, unknown>) {
		super.updated(changedProperties);
		this.codeEditor.content = await this.adapter.getContent();
		this.variablesGrid?.setSourceData(await this.createGridData());
	}

	async _onApplyClick() {
		const newFormula = this.codeEditor.content.replace(/[\r\n]/g, '');
		await this.adapter?.save(newFormula);
		if (this.options.closeAfterSave) this.dispatchEvent(new Event('close'));
	}

	_onCloseClick() {
		this.dispatchEvent(new Event('close'));
	}

	async createGridData(): Promise<GridSource> {
		this.loader.show(this.variablesDiv);
		const gridData: GridSource = {};
		gridData.options = {
			editable: false,
			colLetters: false,
			rowNumbers: false,
			filters: true,
			columnSorting: true,
			fontSize: 12,
			rowHeight: 27
		};
		gridData.columns = [{
				t: 'a',
				e: false,
				w: 25,
				content: [{
					action: 'addVar',
					title: msg('Paste'),
					icon: 'paste',
					color: 'blue',
				}],
				filter: false
			}, {
				data: 'variable',
				header: msg('Variable'),
				t: 't',
				e: false,
				filter: true,
				w: 150
			}, {
				data: 'description',
				header: msg('Description'),
				t: 't',
				e: false,
				w: 300
			}
		]
		const variables = await this.adapter.getVariables();
		gridData.rows = variables.map(variable => ({ data: {
				variable: variable.variable,
				description: variable.description || null
		}}));
		this.loader.hide();
		return gridData;
	}

	_onGridAction = (e: GridEvent) => {
		switch (e.action) {
			case 'addVar':
				this.addText(e.cell.row.data.variable, true); 
				break;
		}
	}

	addText(text: string = null, isVariable = false) {
		if (text) {
			this.codeEditor.content += isVariable ? `{${text}}` : text;
		} else if (this.codeEditor.content.length > 0 && !isVariable) { // delete mode
			const lastChar = this.codeEditor.content.slice(-1);
			if (lastChar == '}') {
				const start = this.codeEditor.content.lastIndexOf('{');
				this.codeEditor.content = this.codeEditor.content.slice(0, start);
			} else {				
				this.codeEditor.content = this.codeEditor.content.slice(0, -1);
			}
		}
		//this.codeEditor.focus();
	}

	render = () => html`
		<div class="controls">
			<og-button
					@click="${() => this._onApplyClick()}"
					icon="floppy-disk"
					color="green"
					.text="${msg('Apply changes')}">
			</og-button>
			<og-button
					@click="${() => this._onCloseClick()}"
					icon="xmark"
					color="red"
					.text="${msg('Close')}">
			</og-button>
		</div>
		<og-code
			zIndex="1000"
			.options="${CODE_EDITOR_OPTIONS}">
		</og-code>
		${this.options?.toolbar ? html`
		<div class="controls">
			<og-button
					@click="${() => this.addText('+')}"
					icon="plus">
			</og-button>
			<og-button
					@click="${() => this.addText('-')}"
					icon="minus">
			</og-button>
			<og-button
					@click="${() => this.addText('*')}"
					icon="xmark">
			</og-button>
			<og-button
					@click="${() => this.addText('/')}"
					icon="divide">
			</og-button>
			<og-button
					@click="${() => this.addText('(')}"
					icon="bracket-round">
			</og-button>
			<og-button
					@click="${() => this.addText(')')}"
					icon="bracket-round-right">
			</og-button>
			<og-button
					@click="${() => this.addText(':')}"
					icon="colon">
			</og-button>
			<og-button
					@click="${() => this.addText('1')}"
					icon="1">
			</og-button>
			<og-button
					@click="${() => this.addText(null)}"
					color="red"
					icon="delete-left">
			</og-button>
		</div>
		` : ``}
		<div class="variables" id="variables">
			${this.options?.variablesGrid ? html`
			<og-overlay defaultLoader></og-overlay>
			<og-grid
				@grid.action="${this._onGridAction}">
			</og-grid>
			` : ``}
		</div>
	`;
}
